import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import ApplicantDashboard from './pages/ApplicantDashboard';
import AdminDashboard from './pages/AdminDashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import DestinationsPage from './pages/DestinationsPage';
import ApplicationWizard from './pages/ApplicationWizard';
import MyApplications from './pages/MyApplications';
import ApplicationDetail from './pages/ApplicationDetail';
import AdminApplications from './pages/AdminApplications';
import AdminCountries from './pages/AdminCountries';
import AdminVisaTypes from './pages/AdminVisaTypes';
import AdminUsers from './pages/AdminUsers';
import AdminContacts from './pages/AdminContacts';
import ProfilePage from './pages/ProfilePage';
import ContactPage from './pages/ContactPage';
import VisaRequirementsPage from './pages/VisaRequirementsPage';
import VisaTypesPage from './pages/VisaTypesPage';
import NotificationsPage from './pages/NotificationsPage';
import B2BRegisterPage from './pages/B2BRegisterPage';
import B2BDashboard from './pages/B2BDashboard';
import AdminB2BPartners from './pages/AdminB2BPartners';
import AdminPartnerApplications from './pages/AdminPartnerApplications';
import JoinUsPage from './pages/JoinUsPage';
import PartnerLoginPage from './pages/PartnerLoginPage';
import AdminLoginPage from './pages/AdminLoginPage';

import './App.css';

// App Router with OAuth handling
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment - handle OAuth callback
  // This must happen synchronously before normal routes render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/secure-login" element={<AdminLoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/countries" element={<DestinationsPage />} />
      <Route path="/countries/:countrySlug" element={<VisaRequirementsPage />} />
      <Route path="/visa-types" element={<VisaTypesPage />} />
      <Route path="/contact" element={<ContactPage />} />
      {/* Legacy redirects */}
      <Route path="/destinations" element={<Navigate to="/countries" replace />} />
      <Route path="/destinations/:countryId" element={<Navigate to="/countries" replace />} />
      <Route path="/visa-requirements/:countryId" element={<VisaRequirementsPage />} />

      {/* Applicant Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={['applicant']}>
            <ApplicantDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apply"
        element={
          <ProtectedRoute>
            <ApplicationWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-applications"
        element={
          <ProtectedRoute>
            <MyApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/applications/:applicationId"
        element={
          <ProtectedRoute>
            <ApplicationDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/applications"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/applications/:applicationId"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ApplicationDetail isAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/countries"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCountries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/countries/new"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCountries isNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/visa-types"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminVisaTypes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/visa-types/new"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminVisaTypes isNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/contacts"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminContacts />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/employees/new"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUsers isNewEmployee />
          </ProtectedRoute>
        }
      />

      {/* Employee Routes */}
      <Route
        path="/employee"
        element={
          <ProtectedRoute allowedRoles={['employee', 'admin']}>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/applications"
        element={
          <ProtectedRoute allowedRoles={['employee', 'admin']}>
            <AdminApplications isEmployee />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/applications/:applicationId"
        element={
          <ProtectedRoute allowedRoles={['employee', 'admin']}>
            <ApplicationDetail isEmployee />
          </ProtectedRoute>
        }
      />

      {/* B2B Partner Routes */}
      <Route path="/partner/register" element={<B2BRegisterPage />} />
      <Route path="/partner/login" element={<PartnerLoginPage />} />
      <Route path="/partner" element={<JoinUsPage />} />
      <Route
        path="/partner/dashboard"
        element={
          <ProtectedRoute allowedRoles={['b2b_partner']}>
            <B2BDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/applications"
        element={
          <ProtectedRoute allowedRoles={['b2b_partner']}>
            <MyApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/apply"
        element={
          <ProtectedRoute allowedRoles={['b2b_partner']}>
            <ApplicationWizard isB2B />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/b2b-partners"
        element={<Navigate to="/admin/partners" replace />}
      />
      <Route
        path="/admin/partners"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminB2BPartners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/partners/:partnerId/applications"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPartnerApplications />
          </ProtectedRoute>
        }
      />
      {/* Legacy redirects */}
      <Route path="/join-us" element={<Navigate to="/partner" replace />} />
      <Route path="/b2b/register" element={<Navigate to="/partner/register" replace />} />
      <Route path="/b2b" element={<Navigate to="/partner/dashboard" replace />} />
      <Route path="/b2b/apply" element={<Navigate to="/partner/apply" replace />} />

      {/* Fallback */}
      <Route path="*" element={<LandingPage />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
